import { useMemo } from 'react';
import { SESSION_MODE_LABEL, type CueItem, type CueStep } from '@micplot/shared';
import { api } from '../lib/api';
import { useStore, useCues, useCanEdit, useViewPos } from '../state/store';

/**
 * Where the show is, on every view.
 *
 * In set-list mode the running order drives the board, but the position only
 * existed on the Cues view -- so an A2 working the board had no idea which
 * number was up or what came next, and had to leave the thing they were using
 * to find out. Advancing lives here too, since the person watching the board is
 * often the person calling it.
 *
 * It also carries the follow switch. Checking what is coming up is something
 * one position does alone; moving the show is something everyone lives with.
 * Those were the same button, so looking ahead meant advancing the show for
 * the whole building. Now stepping while detached moves only this screen, and
 * putting something on the board stays a separate, deliberate act.
 */
export default function NowBar() {
  const identity = useStore(s => s.identity)!;
  const session = useStore(s => s.state?.session);
  const notify = useStore(s => s.notify);
  const following = useStore(s => s.following);
  const setFollowing = useStore(s => s.setFollowing);
  const setPreviewPos = useStore(s => s.setPreviewPos);
  const viewPos = useViewPos();
  const cues = useCues();
  const canEdit = useCanEdit();

  const flat = useMemo(() => {
    const out: { cue: CueItem; step: CueStep }[] = [];
    for (const c of cues) for (const st of c.steps) out.push({ cue: c, step: st });
    return out;
  }, [cues]);

  // Only set-list mode has a position worth showing; elsewhere the board is truth.
  if (!session || session.mode !== 'set-list' || flat.length === 0) return null;

  const showPos = session.playheadPos;
  const pos = Math.min(viewPos, flat.length - 1);
  const here = flat[pos];
  const showing = flat[showPos];
  const next = flat[pos + 1];
  if (!here) return null;

  const detached = !following && pos !== showPos;

  const run = (p: Promise<unknown>) =>
    p.catch(e => notify((e as Error).message, 'error'));

  /** Stepping moves the show when following, and only this screen when not. */
  const move = (delta: -1 | 1) => {
    if (following) {
      void run(api.playhead(identity.code, delta === 1 ? 'next' : 'prev'));
      return;
    }
    setPreviewPos(Math.max(0, Math.min(pos + delta, flat.length - 1)));
  };

  const stepLabel = (e: { cue: CueItem; step: CueStep }) =>
    e.cue.steps.length > 1 ? ` (change ${e.step.position + 1}/${e.cue.steps.length})` : '';

  return (
    <>
      {/*
        * The detached state has to be loud. Believing you are driving the show
        * when you are not is a far worse failure than any inconvenience of
        * saying so, so it gets its own bar and names both positions.
        */}
      {detached && (
        <div className="previewbar" role="status">
          <span className="previewbar-tag">PREVIEWING</span>
          <span className="previewbar-text">
            <strong>{here.cue.label}{stepLabel(here)}</strong>
            <span className="previewbar-sep">·</span>
            the show is on <strong>{showing?.cue.label ?? '—'}</strong>
          </span>
          <button className="btn sm" onClick={() => setFollowing(true)}>
            Back to the show
          </button>
        </div>
      )}

      <div className={`nowbar${detached ? ' detached' : ''}`}>
        <span className="nowbar-badge">{SESSION_MODE_LABEL['set-list']}</span>

        <span className="nowbar-now">
          <span className="nowbar-k">{detached ? 'Looking at' : 'Now'}</span>
          <strong>{here.cue.label}</strong>
          {here.cue.steps.length > 1 && (
            <span className="chip">
              change {here.step.position + 1}/{here.cue.steps.length}
            </span>
          )}
        </span>

        {next && (
          <span className="nowbar-next">
            <span className="nowbar-k">Next</span>
            {next.cue.id === here.cue.id
              ? (next.step.label || `change ${next.step.position + 1}`)
              : next.cue.label}
          </span>
        )}

        <span className="count">{pos + 1} / {flat.length}</span>

        <button className="btn sm" title={following ? 'Follow the show' : 'Look around on your own'}
                aria-pressed={!following}
                onClick={() => setFollowing(!following)}>
          {following ? '🔗 Following' : '⏸ On your own'}
        </button>

        <button className="btn sm" disabled={pos <= 0 || (following && !canEdit)}
                onClick={() => move(-1)}>◀</button>
        <button className="btn sm primary"
                disabled={pos >= flat.length - 1 || (following && !canEdit)}
                onClick={() => move(1)}>Next ▶</button>

        {/*
          * Detached, the two things that touch everyone else get their own
          * buttons and their own words. Neither can happen by reflex while
          * looking ahead.
          */}
        {detached && canEdit && (
          <>
            <button className="btn sm"
                    title="Put the assignments you are looking at onto the live board"
                    /*
                     * Say what actually happened. An unprogrammed number is
                     * deliberately a no-op server-side, so toasting success
                     * regardless told an A2 the look was live when the
                     * previous cue was still on the air -- the precise
                     * misapprehension this whole detached mode exists to
                     * prevent.
                     */
                    onClick={() => void run(
                      api.applyStep(identity.code, here.step.id)
                        .then(r => notify(
                          r.applied
                            ? `Put ${here.cue.label} on the board.`
                            : `${here.cue.label} has no mics set — nothing to put up.`,
                          r.applied ? 'info' : 'error',
                        )),
                    )}>
              Put this on the board
            </button>
            <button className="btn sm"
                    title="Move the show on for everyone"
                    onClick={() => void run(api.playhead(identity.code, 'next'))}>
              Advance the show
            </button>
          </>
        )}
      </div>
    </>
  );
}
